<template>
  <div>
    <header class="flex align-items-center justify-content-between mb-3">
      <div>
        <h2 class="m-0 p-0 font-light text-600">Edit Attendance</h2>
        <p v-if="!routeError" class="m-0 pt-1 text-xs text-300">
          Attendance #{{ selectedIds.attendance_id }}
        </p>
      </div>
      <Button
        label="Back"
        icon="pi pi-arrow-left"
        class="surface-300 hover:surface-500"
        @click="handleBack"
      />
    </header>

    <div v-if="routeError" class="card">
      <Message severity="error" :closable="false">{{ routeError }}</Message>
    </div>

    <div v-else class="card overflow-x-auto">
      <AttendanceUpdateDialog
        :selectedIds="selectedIds"
        @updateSuccess="handleUpdateSuccess"
        @deleteSuccess="handleUpdateSuccess"
        @handleBack="handleBack"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'primevue/button';
import Message from 'primevue/message';
import AttendanceUpdateDialog from '@/components/dialogs/AttendanceUpdateDialog.vue';
import {
  parseAttendanceRouteFilters,
  parseAttendanceSortQuery
} from '@/composables/useAttendanceRouteFilters';
import {
  parseRosterRouteFilters,
  parseRosterSortQuery
} from '@/composables/useRosterRouteFilters';
import { parseDashboardRouteDate } from '@/composables/useDashboardRoute';

const route = useRoute();
const router = useRouter();

const parsedRoute = computed(() => {
  const parameterNames = ['employeeId', 'rosterId', 'attendanceId'];
  const parsedValues = {};

  for (const parameterName of parameterNames) {
    const rawValue = route.params[parameterName];
    if (typeof rawValue !== 'string' || !/^\d+$/.test(rawValue)) {
      return { values: null, error: 'Attendance identifiers must be positive integers.' };
    }
    const value = Number(rawValue);
    if (!Number.isSafeInteger(value) || value <= 0) {
      return { values: null, error: 'One or more attendance identifiers are invalid.' };
    }
    parsedValues[parameterName] = value;
  }

  return { values: parsedValues, error: null };
});

const routeError = computed(() => parsedRoute.value.error);
const selectedIds = computed(() => ({
  employee_id: parsedRoute.value.values?.employeeId,
  roster_id: parsedRoute.value.values?.rosterId,
  attendance_id: parsedRoute.value.values?.attendanceId
}));

const safeReturnTo = computed(() => {
  const returnTo = route.query.returnTo;
  if (
    typeof returnTo !== 'string'
    || !returnTo.startsWith('/')
    || returnTo.startsWith('//')
  ) {
    return null;
  }

  const resolvedRoute = router.resolve(returnTo);
  if (resolvedRoute.name === 'DashboardDay') {
    const parsedDate = parseDashboardRouteDate(resolvedRoute.params);
    return parsedDate.valid ? resolvedRoute.fullPath : null;
  }

  if (resolvedRoute.name === 'ViewAttendanceFiltered') {
    const parsedFilters = parseAttendanceRouteFilters(resolvedRoute.params);
    const parsedSort = parseAttendanceSortQuery(resolvedRoute.query);
    return parsedFilters.valid && parsedSort.valid ? resolvedRoute.fullPath : null;
  }

  if (resolvedRoute.name === 'ViewRosterFiltered') {
    const parsedFilters = parseRosterRouteFilters(resolvedRoute.params, 'roster');
    const parsedSort = parseRosterSortQuery(resolvedRoute.query);
    return parsedFilters.valid && parsedSort.valid ? resolvedRoute.fullPath : null;
  }

  return null;
});

const navigateBackOrFallback = replaceFallback => {
  if (safeReturnTo.value) {
    router.replace(safeReturnTo.value);
    return;
  }

  if (window.history.state?.back) {
    router.back();
    return;
  }

  if (replaceFallback) router.replace({ name: 'ViewAttendance' });
  else router.push({ name: 'ViewAttendance' });
};

const handleBack = () => navigateBackOrFallback(false);
const handleUpdateSuccess = () => navigateBackOrFallback(true);
</script>
